import * as _ from 'lodash'
import * as React from 'react'
import * as Material from 'react-materialize'
import { Button, Col, Row } from 'reactstrap'
import { userData } from '../../../../util/userData'
import { NewProjectHttp } from '../../http'
import { IProject, ISurveyReference } from '../../interfaces'

interface ISurveyPickerProps {
  values: ISurveyReference[]
  onChange: (values: ISurveyReference[]) => void
}

interface ISurveyPickerState {
  projects: IProject[]
  surveys: ISurveyReference[]
}

export class SurveyPicker extends React.Component<ISurveyPickerProps, ISurveyPickerState> {
  private static cachedProjects: IProject[] = null
  private static cachedSurveys: ISurveyReference[] = null

  state: ISurveyPickerState = {
    projects: [],
    surveys: []
  }

  componentWillMount() {
    this.loadSurveys()
  }

  loadSurveys() {
    const addSurveyIfEmpty = () => {
      if (this.props.values.length === 0) {
        this.addSurvey()
      }
    }

    if (SurveyPicker.cachedProjects && SurveyPicker.cachedSurveys) {
      const projects = _.cloneDeep(SurveyPicker.cachedProjects)
      const surveys = _.cloneDeep(SurveyPicker.cachedSurveys)
      this.setState({ projects, surveys }, () => addSurveyIfEmpty())
    } else {
      NewProjectHttp.getSurveys().then((projects) => {
        const currentProjectAbbr = userData.getCurrentProject()

        projects.sort((p1, p2) => {
          const s1IsInDefaultProject = p1.abbreviation === currentProjectAbbr
          const s2IsInDefaultProject = p2.abbreviation === currentProjectAbbr

          if (s1IsInDefaultProject && !s2IsInDefaultProject) {
            return -1
          } else if (!s1IsInDefaultProject && s2IsInDefaultProject) {
            return 1
          } else {
            return 0
          }
        })

        const surveys = projects
          //get survey refs
          .map((p) => p.surveyReferences)
          //flatten out array
          .reduce((accumulator, current) => accumulator.concat(current))

        SurveyPicker.cachedProjects = projects
        SurveyPicker.cachedSurveys = surveys

        this.setState({ projects, surveys }, () => addSurveyIfEmpty())
      })
    }
  }

  addSurvey() {
    const { surveys } = this.state
    const { values } = this.props
    const nextSurvey = surveys.find((survey) => !this.isSurveyInValues(survey))
    const newSurvey = _.cloneDeep(nextSurvey)
    newSurvey.surveyNewAbbreviation = newSurvey.surveyOldAbbreviation
    const newValues = [...values, newSurvey]

    this.props.onChange(newValues)
  }

  updateSurvey(index: number, abbreviation: string, surveyId?: string, abbr?: string) {
    const { surveys } = this.state
    const newValues = [...this.props.values]
    let newSurvey = _.clone(surveys
      .filter((survey) => abbreviation === survey.client)
      .find((survey) => !this.isSurveyInValues(survey))
    )

    if (surveyId) {
      newSurvey = this.getSurveyFromUniqueId(surveyId)
      newSurvey.surveyNewAbbreviation = newSurvey.surveyOldAbbreviation
    }

    if (abbr) {
      newSurvey.surveyNewAbbreviation = abbr
    }

    if (newSurvey.surveyOldAbbreviation && !newSurvey.surveyNewAbbreviation) {
      newSurvey.surveyNewAbbreviation = newSurvey.surveyOldAbbreviation
    }

    newValues.splice(index, 1, newSurvey)

    this.props.onChange(newValues)
  }

  removeSurvey(index: number) {
    const newValues = [...this.props.values]

    newValues.splice(index, 1)

    this.props.onChange(newValues)
  }

  getSurveyRefsFor(survey: ISurveyReference): ISurveyReference[] {
    const surveysWithCurrentClient = this.state.surveys
      .filter((s) => s.client === survey.client)
      .filter((s) => !this.isSurveyInValues(s))

    surveysWithCurrentClient.push(survey)

    return surveysWithCurrentClient
  }

  isSurveyInValues(survey: ISurveyReference) {
    const { values } = this.props

    return values.some((value) => this.isSameSurvey(value, survey))
  }

  isSameSurvey(s1: ISurveyReference, s2: ISurveyReference) {
    return s1.surveyOldAbbreviation === s2.surveyOldAbbreviation &&
      s1.client === s2.client &&
      s1.surveyLabel === s2.surveyLabel
  }

  isSameSurvey2(s: ISurveyReference, surveyLabel: string, surveyOldAbbreviation: string, client: string) {
    return s.surveyLabel === surveyLabel &&
      s.surveyOldAbbreviation === surveyOldAbbreviation &&
      s.client === client
  }

  getSurveyUniqueId(survey: ISurveyReference) {
    return `${survey.surveyLabel}###${survey.surveyOldAbbreviation}###${survey.client}`
  }

  getSurveyFromUniqueId(uniqueId: string) {
    const { surveys } = this.state
    const { values } = this.props
    const surveyParts = uniqueId.split('###')
    const surveyLabel = surveyParts[0]
    const surveyOldAbbreviation = surveyParts[1]
    const client = surveyParts[2]
    const value = values.find((s) => this.isSameSurvey2(s, surveyLabel, surveyOldAbbreviation, client))

    if (value) {
      return value
    } else {
      return surveys.find((s) => this.isSameSurvey2(s, surveyLabel, surveyOldAbbreviation, client))
    }
  }

  surveyReferencesHaveUniqueAbbr() {
    const { values } = this.props

    const uniqueSurveysByNewAbbr = _.uniqBy(values, 'surveyNewAbbreviation')

    return values.length === uniqueSurveysByNewAbbr.length
  }

  setUniqueInputValidation(uniqueRef: HTMLInputElement) {
    if (uniqueRef !== null) {
      uniqueRef.setCustomValidity('Survey Abbreviations must be unique.')
    }
  }

  render() {
    const surveyAbbrAreUnique = this.surveyReferencesHaveUniqueAbbr()

    return (
      <section>
        <article style={{ minHeight: 81 }}>
          {this.props.values.map((value, index) => {
            const uniqueValue = this.getSurveyUniqueId(value)
            return (
              <Row key={index}>
                <Col sm={4}>
                  <Material.Input type="select" label="Client"
                    onChange={(e) => this.updateSurvey(index, e.currentTarget.value)}
                    value={value.client}
                    required>
                    {this.state.projects.map((survey) =>
                      <option key={survey.abbreviation} value={survey.abbreviation}>{survey.projectName}</option>
                    )}
                  </Material.Input>
                </Col>
                <Col sm={4}>
                  <Material.Input type="select" label="Survey"
                    onChange={(e) => this.updateSurvey(index, value.client, e.currentTarget.value)}
                    value={uniqueValue}
                    required>
                    {this.getSurveyRefsFor(value).map((surveyRef) => {
                      const surveyRefId = this.getSurveyUniqueId(surveyRef)
                      return (
                        <option key={surveyRefId} value={surveyRefId}>{surveyRef.surveyLabel}</option>
                      )
                    })}
                  </Material.Input>
                </Col>
                <Col sm={3}>
                  <Material.Input label="Survey Abbreviation"
                    onChange={(e) => this.updateSurvey(index, value.client, uniqueValue, e.currentTarget.value)}
                    value={value.surveyNewAbbreviation}
                    required />
                </Col>
                {this.props.values.length > 1 &&
                  <Col sm={1} className="d-flex align-items-center">
                    <Button color="link" className="bl-btn" onClick={() => this.removeSurvey(index)}>
                      <i className="fas fa-fw fa-times text-muted" />
                    </Button>
                  </Col>
                }
              </Row>
            )
          })}
        </article>

        {!surveyAbbrAreUnique &&
          <Row>
            <Col sm={11}>
              <article className="text-danger d-flex justify-content-end pb-2">
                <span>Survey Abbreviations must be unique.</span>
                <input type="text" required minLength={5} value=""
                  ref={(ref) => this.setUniqueInputValidation(ref)}
                  style={{ opacity: 0, height: 1, width: 1 }} />
              </article>
            </Col>
          </Row>
        }

        <article className="mb-3">
          <Button color="success" className="bl-btn" onClick={() => this.addSurvey()}>
            <i className="fas fa-fw fa-plus-square" />&nbsp;
            <span>Add Survey</span>
          </Button>
        </article>
      </section>
    )
  }
}
